import type { IntakeSpec, ArchitectureInteriorRoomDetails } from '@shared/types';
import type { Part, Joint, Feature } from '@shared/spec';

function getDetails(intake: IntakeSpec): ArchitectureInteriorRoomDetails {
  return intake.details as ArchitectureInteriorRoomDetails;
}

function scaleNumber(scaleRatio: string): number {
  // e.g., '1:50' -> 50
  return parseInt(scaleRatio.split(':')[1], 10);
}

export function parts(intake: IntakeSpec): Part[] {
  const d = getDetails(intake);
  const scale = scaleNumber(d.scaleRatio);

  // Real-world dimensions in mm
  const realLength = intake.core.size?.length ?? 4000; // 4m default
  const realWidth = intake.core.size?.width ?? 3500;   // 3.5m default
  const realCeilingHeight = d.ceilingHeight;           // already in mm from details
  const wallThickness = 3; // model material thickness, NOT scaled

  const modelLength = realLength / scale;
  const modelWidth = realWidth / scale;
  const modelHeight = realCeilingHeight / scale;

  const out: Part[] = [];

  // Floor panel
  out.push({
    id: 'floor',
    name: '床',
    kind: 'panel',
    materialId: 'mat-primary',
    width: modelLength,
    depth: modelWidth,
    thickness: wallThickness,
    position: [0, 0, 0],
    rotation: [0, 0, 0],
    tags: ['floor'],
    paramRefs: [],
    cutouts: [],
  });

  // North wall
  out.push({
    id: 'wall_north',
    name: '北壁',
    kind: 'wall',
    materialId: 'mat-primary',
    width: modelLength,
    height: modelHeight,
    thickness: wallThickness,
    position: [0, modelWidth / 2, 0],
    rotation: [0, 0, 0],
    tags: ['wall'],
    openings: [],
    paramRefs: [],
  });

  // South wall
  out.push({
    id: 'wall_south',
    name: '南壁',
    kind: 'wall',
    materialId: 'mat-primary',
    width: modelLength,
    height: modelHeight,
    thickness: wallThickness,
    position: [0, -(modelWidth / 2), 0],
    rotation: [0, 0, 0],
    tags: ['wall'],
    openings: [],
    paramRefs: [],
  });

  // East wall
  out.push({
    id: 'wall_east',
    name: '東壁',
    kind: 'wall',
    materialId: 'mat-primary',
    width: modelWidth,
    height: modelHeight,
    thickness: wallThickness,
    position: [modelLength / 2, 0, 0],
    rotation: [0, 0, 90],
    tags: ['wall'],
    openings: [],
    paramRefs: [],
  });

  // West wall
  out.push({
    id: 'wall_west',
    name: '西壁',
    kind: 'wall',
    materialId: 'mat-primary',
    width: modelWidth,
    height: modelHeight,
    thickness: wallThickness,
    position: [-(modelLength / 2), 0, 0],
    rotation: [0, 0, 90],
    tags: ['wall'],
    openings: [],
    paramRefs: [],
  });

  // Optional ceiling panel
  if (d.showCeiling) {
    out.push({
      id: 'ceiling',
      name: '天井',
      kind: 'panel',
      materialId: 'mat-primary',
      width: modelLength,
      depth: modelWidth,
      thickness: wallThickness,
      position: [0, 0, modelHeight],
      rotation: [0, 0, 0],
      tags: ['ceiling'],
      paramRefs: [],
      cutouts: [],
    });
  }

  // Optional furniture stub
  if (d.showFurniture) {
    out.push({
      id: 'furniture_stub',
      name: '家具（プレースホルダー）',
      kind: 'custom',
      materialId: 'mat-primary',
      openscadSnippet: '// generic furniture placeholder',
      boundingBox: { w: 60, d: 30, h: 40 },
      position: [0, 0, wallThickness],
      rotation: [0, 0, 0],
      tags: ['furniture'],
      paramRefs: [],
    });
  }

  return out;
}

export function joints(_intake: IntakeSpec): Joint[] {
  return [];
}

export function features(_intake: IntakeSpec): Feature[] {
  return [];
}
